import React, { useEffect, useState } from "react";
import PageTitleLayout from "../components/layouts/PageLayouts/PageTitleLayout";
import { Box, Typography, useTheme } from "@mui/material";
import ProgramCard from "../components/pageSpecific/NewDemands/ProgramCard/ProgramCard";
import ProgramCardSkeletton from "../components/pageSpecific/NewDemands/ProgramCard/ProgramCardSkeletton";
import useUser from "../hooks/useUserData";
import { AccountRow } from "../data/Account/models";
import NewDemandDialog from "../components/pageSpecific/NewDemands/ProgramCard/NewDemandDialog/NewDemandDialog";
import { ClosedAccountPrograms, ProgramRow } from "../data/Program/models";
import CustomButton from "../components/common/buttons/CustomButton";
import { ServiceRow } from "../data/Service/models";
import BoxResponsivePadding from "../components/common/boxes/BoxResponsivePadding";
import { QueryError } from "../types";
import { useQuery } from "@tanstack/react-query";
import { getPrograms } from "../data/Program/queries";
import { getAccount } from "../data/Account/queries";
import QueryErrorMessage from "../components/common/error/QueryErrorMessage";
import LockIcon from "@mui/icons-material/Lock";

const NewDemands = () => {
  const programQuery = useQuery<
    ProgramRow[] | ClosedAccountPrograms,
    QueryError
  >({
    queryKey: ["Programs"],
    queryFn: () => getPrograms(),
    staleTime: 600000,
    gcTime: 600000,
  });
  const accountQuery = useQuery<AccountRow, QueryError>({
    queryKey: ["Account"],
    queryFn: () => getAccount(),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ServiceRow>();
  const { userData } = useUser();

  const handleTypeClick = (type: ServiceRow) => {
    if (selectedType !== type) setSelectedType(type);
    setDialogOpen(true);
  };

  const isLoading = programQuery.isFetching || accountQuery.isLoading;
  const error = programQuery.error ?? accountQuery.error;

  if (isLoading) return <NewDemandsLoading />;

  if (error || !programQuery.data || !accountQuery.data)
    return <QueryErrorMessage error={error} />;

  if ("reason" in programQuery.data) {
    if (programQuery.data.reason === "closed") {
      return (
        <Box
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          padding="16px"
          boxSizing="border-box"
        >
          <LockIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
          <Typography variant="h4" color="primary" fontWeight="bold" mb={2}>
            Votre organisation n'est pas autorisée à créer de nouvelles demandes.
          </Typography>
          <Typography variant="h5" color="primary" mb={2}>
            Pour obtenir de l'aide, veuillez contacter votre répondant municipal.
          </Typography>
        </Box>
      );
    } else {
      return <QueryErrorMessage error={error} />;
    }
  }

  const contactProgram = programQuery.data.find(
    (program) =>
      program.vdl_name === "Vous ne trouvez pas ce que vous cherchez ?"
  );

  return (
    <PageTitleLayout title="Nouvelle demande">
      <BoxResponsivePadding>
        {selectedType ? (
          <NewDemandDialog
            selectedType={selectedType}
            open={dialogOpen}
            setOpen={setDialogOpen}
            contacts={accountQuery.data.contacts}
          />
        ) : null}

        <Box display="grid" gap="16px">
          {programQuery.data.slice(0).sort((a, b) => a.vdl_name.localeCompare(b.vdl_name)).map((program) => {
            if (
              program.vdl_programmeid !== contactProgram?.vdl_programmeid &&
              program.services.length > 0
            ) {
              return (
                <ProgramCard
                  key={program.vdl_programmeid}
                  program={program}
                  handleTypeClick={handleTypeClick}
                />
              );
            }
          })}
          {contactProgram && contactProgram.services[0] ? (
            <Box
              bgcolor="primary.main"
              padding="1rem"
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap"
              gap="16px"
            >
              <Typography color="common.white" variant="h6" fontSize="bold">
                {contactProgram.vdl_name}
              </Typography>
              <CustomButton
                onClick={() => handleTypeClick(contactProgram.services[0])}
                sx={{ color: "common.white", border: "1px solid white" }}
              >
                {contactProgram.services[0].vdl_name}
              </CustomButton>
            </Box>
          ) : null}
        </Box>
      </BoxResponsivePadding>
    </PageTitleLayout>
  );
};

export default NewDemands;

const NewDemandsLoading = () => {
  const theme = useTheme();
  return (
    <PageTitleLayout title={"Nouvelle demande"} isLoading>
      <Box
        sx={{
          ...theme.custom.pageCardContainer,
        }}
        display="grid"
        gap="16px"
      >
        {Array(4)
          .fill(0)
          .map((i, idx) => (
            <ProgramCardSkeletton key={idx} />
          ))}
      </Box>
    </PageTitleLayout>
  );
};
